import { useEffect, useMemo, useState } from 'react';
import { Steps } from 'intro.js-react';

import { LocalStore } from 'constants/localstore';
import { useAppDispatch, useAppSelector } from 'store/store-hooks';
import { useLocation, Path } from 'components/_hooks/use-router';
import { setIntro } from 'components/app/_mediator/app.slice';
import { setConfigPanel } from 'components/app/editor/_mediator/editor.slice';

import { TUTS_ADD_TAG } from './tuts/_tuts-add-tag';
import { TUTS_CLIP } from './tuts/_tuts-clip';
import { TUTS_SB_TRACK } from './tuts/_tuts-sb-track';
import { TUTS_AUDIO_TRACK } from './tuts/_tuts-audio-track';
import { TUTS_SB_SETTINGS } from './tuts/_tuts-sb-settings';
import { TUTS_PROJECTS } from './tuts/_tuts-projects';
import { getFinalCutsTutorial, isFinalCutsTutorial } from './tuts/_tuts-fc';
import { IntroKeys } from './intro-keys';
import { STEPS_SR } from './_intro-sr';
import { STEPS_SB } from './_intro-sb';
import { STEPS_FC } from './_intro-fc';
import { STEPS_PULSE } from './_intro-pulse';
import { STEPS_PROJECTS } from './_intro-projects';

export interface StartTour {
  intro: boolean;
  projects: boolean;
  sr: boolean;
  sb: boolean;
  fc: boolean;
}

export const Intro = () => {
  const { path } = useLocation();
  const finalcuts = useAppSelector((state) => state.app__data.finalcuts);
  const appRestored = useAppSelector((state) => state.app.appRestored);
  const beginnerMode = useAppSelector((state) => state.app.beginnerMode);
  const configPanel = useAppSelector((state) => state.editor.configPanel);
  const intro = useAppSelector((state) => state.app.intro);
  const dispatch = useAppDispatch();

  const [initStep, setInitStep] = useState(0);

  const steps = useMemo(() => {
    if (intro === false) return [];
    setInitStep(0);

    /* intro steps */
    if (intro === true) {
      switch (path) {
        case Path.pulseIntro:
          return STEPS_PULSE;
        case Path.projects:
          return STEPS_PROJECTS;
        case Path.selector: {
          if (configPanel) dispatch(setConfigPanel(null));
          return STEPS_SR;
        }
        case Path.storyboard:
          return STEPS_SB;
        case Path.finalcuts:
          return STEPS_FC;
        default:
          return [];
      }
    }
    /* tutorials steps */
    switch (intro) {
      case IntroKeys.srAddClip:
        return TUTS_ADD_TAG;
      case IntroKeys.sbClip:
        return TUTS_CLIP;
      case IntroKeys.storyBoardHeader:
        return TUTS_SB_TRACK;
      case IntroKeys.sbAudio:
        return beginnerMode ? TUTS_AUDIO_TRACK.filter((step) => step.name !== 'lengthenCell') : TUTS_AUDIO_TRACK;
      case IntroKeys.sbAudioHeader:
        return beginnerMode ? TUTS_AUDIO_TRACK.filter((step) => step.name !== 'lengthenCell') : TUTS_AUDIO_TRACK;
      case IntroKeys.sbSettings:
        return TUTS_SB_SETTINGS;
      case IntroKeys.createProject:
      case IntroKeys.addFiles:
      case IntroKeys.createStory:
        setInitStep(TUTS_PROJECTS.findIndex((step) => step.element === `[data-intro="${intro}"]`));
        return TUTS_PROJECTS;
    }
    if (isFinalCutsTutorial(intro)) {
      const finalCutsTutorial = getFinalCutsTutorial(intro);
      if (finalCutsTutorial) return finalCutsTutorial;
    }
    return [];
  }, [intro, beginnerMode]);

  useEffect(() => {
    if (intro === false) return;
    const onClick = (e: MouseEvent) => {
      if ((e.target as HTMLElement).closest('.introjs-tooltip')) return;
      e.stopImmediatePropagation();
      dispatch(setIntro(false));
    };
    document.addEventListener('click', onClick, true);
    return () => {
      document.removeEventListener('click', onClick, true);
    };
  }, [intro]);

  useEffect(() => {
    if (!appRestored) return;
    if (finalcuts.length && finalcuts[0].status > 1) return;

    const json = localStorage.getItem(LocalStore.startTour);
    const startTour = json ? (JSON.parse(json) as Partial<StartTour>) ?? {} : {};

    if (path === Path.pulseIntro && !startTour.intro) {
      localStorage.setItem(LocalStore.startTour, JSON.stringify({ ...startTour, intro: true }));
      dispatch(setIntro(true));
      return;
    }
    if (path === Path.projects && !startTour.projects) {
      localStorage.setItem(LocalStore.startTour, JSON.stringify({ ...startTour, projects: true }));
      dispatch(setIntro(true));
      return;
    }
    if (path === Path.selector && !startTour.sr) {
      localStorage.setItem(LocalStore.startTour, JSON.stringify({ ...startTour, sr: true }));
      dispatch(setIntro(true));
      return;
    }
    if (path === Path.storyboard && !startTour.sb) {
      localStorage.setItem(LocalStore.startTour, JSON.stringify({ ...startTour, sb: true }));
      dispatch(setIntro(true));
      return;
    }
    if (path === Path.finalcuts && !startTour.fc) {
      localStorage.setItem(LocalStore.startTour, JSON.stringify({ ...startTour, fc: true }));
      dispatch(setIntro(true));
      return;
    }
  }, [path, appRestored]);

  return (
    <Steps
      enabled={!!steps.length}
      initialStep={initStep}
      steps={steps}
      onExit={() => dispatch(setIntro(false))}
      options={{ showProgress: true, showBullets: true, scrollToElement: true }}
    />
  );
};
